import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import api from '../components/setupAxios';
import API_URL from "../config";
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Tabs, Tab, TextField, ButtonGroup,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Checkbox, Grid
} from '@mui/material';

const AssistantSessionDetails = ({ socket }) => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [remainingTime, setRemainingTime] = useState("00:00:00");
  const [studentId, setStudentId] = useState('');
  const [seatLines, setSeatLines] = useState([{ podId: '', seatId: '' }]);
  const [timeOut, setTimeOut] = useState("");
  const [activeLineIndex, setActiveLineIndex] = useState(null);
  const [borrowedItem, setBorrowedItem] = useState('');
  const [studentLogs, setStudentLogs] = useState([]);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [editingSupervisor, setEditingSupervisor] = useState(false);
  const [newSupervisor, setNewSupervisor] = useState("");
  const canvasRef = useRef(null);
  const bell30MinRef = useRef(null);
  const bellEndRef = useRef(null);
  const bellTestRef = useRef(null);
  const hasPlayed30Min = useRef(false);
  const hasPlayed0Min = useRef(false);
  const [activeTab, setActiveTab] = useState(localStorage.getItem('activeTab') || 'timer');

  const adjustDateToDayOfWeek = (dateString, dayOfWeek) => {
    const date = new Date(dateString);
    const today = new Date();
    const currentDay = today.getDay();
    let daysUntilTarget = dayOfWeek - currentDay;
    if (daysUntilTarget < 0) {
      daysUntilTarget += 7;
    }
    date.setDate(today.getDate() + daysUntilTarget);
    return date.toISOString();
  };

  useEffect(() => {
    fetchSessionDetails();
    fetchStudentLogs();
  }, [sessionId]);

  useEffect(() => {
    if (!socket) return;
    const handleSessionEvent = (data) => {
      console.log("Received session event:", data);
      window.location.reload();
    };
    if (socket.connected) {
      socket.on("sessionCheckIn", handleSessionEvent);
      socket.on("sessionCheckOut", handleSessionEvent);
    } else {
      console.warn("Socket is not connected yet!");
    }
    return () => {
      socket.off("sessionCheckIn", handleSessionEvent);
      socket.off("sessionCheckOut", handleSessionEvent);
    };
  }, [socket]);

  useEffect(() => {
    if (session && session.status === "active") {
      updateRemainingTime(session);
      const interval = setInterval(() => updateRemainingTime(session), 1000);
      return () => clearInterval(interval);
    }
  }, [session]);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  const hasAdjustedPositions = useRef(false);
  useEffect(() => {
    if (!canvasRef.current || !session || !session.pods || session.pods.length === 0 || hasAdjustedPositions.current) return;
    hasAdjustedPositions.current = true;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const newCanvasWidth = rect.width;
    const newCanvasHeight = rect.height;
    const scaleX = newCanvasWidth / 800;
    const scaleY = newCanvasHeight / 600;
    const scaleFactor = Math.min(scaleX, scaleY);
    const centerX = 800 / 2;
    const centerY = 600 / 2;
    const newCenterX = newCanvasWidth / 2;
    const newCenterY = newCanvasHeight / 2;
    const updatedPods = session.pods.map((pod) => {
      const originalX = pod.x ?? Math.random() * 800;
      const originalY = pod.y ?? Math.random() * 600;
      const offsetX = originalX - centerX;
      const offsetY = originalY - centerY;
      const newX = Math.round(newCenterX + offsetX * scaleFactor);
      const newY = Math.round(newCenterY + offsetY * scaleFactor);
      return {
        ...pod,
        x: newX,
        y: newY,
        tableWidth: Math.round(100 * scaleFactor),
        seatSize: Math.round(30 * scaleFactor)
      };
    });
    setSession(prevSession => ({
      ...prevSession,
      pods: updatedPods
    }));
  }, [session?.pods]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchSessionDetails = async () => {
    try {
      const response = await api.get(`/lab-sessions/${sessionId}`);
      setSession(prevSession => {
        if (!response.data.pods || response.data.pods.length === 0) {
          console.warn("Warning: No pods data received!");
          return prevSession;
        }
        return {
          ...prevSession,
          ...response.data,
          pods: [...response.data.pods]
        };
      });
    } catch (error) {
      console.error("Error fetching session details:", error);
    }
  };

  const updateRemainingTime = (sessionData) => {
    if (!sessionData) return;
    const now = new Date();
    const end = new Date(adjustDateToDayOfWeek(sessionData.scheduledEnd, sessionData.dayOfWeek));
    const remaining = end - now;
    if (remaining > 0) {
      const hours = Math.floor(remaining / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setRemainingTime(`${hours}h ${minutes}m ${seconds}s`);
      if (remaining >= 1797000 && remaining <= 1800000 && !hasPlayed30Min.current) {
        hasPlayed30Min.current = true;
        playAlarm(bell30MinRef);
      }
    } else {
      setRemainingTime("Session ended");
      if (remaining >= -3000 && remaining <= 0 && !hasPlayed0Min.current) {
        hasPlayed0Min.current = true;
        playAlarm(bellEndRef);
      }
    }
  };

  const playAlarm = (audioRef) => {
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  const startSession = () => {
    if (session.status !== 'scheduled') {
      alert("You cannot start an ongoing or ended session!");
      return;
    }
    fetch(`${API_URL}/lab-sessions/${sessionId}/start`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    })
      .then(res => res.json())
      .then(() => window.location.reload())
      .catch((err) => alert(`Error starting session: ${err.message || err}`));
  };

  const endSession = () => {
    if (session.status !== 'active') {
      alert("You cannot end a session that has not yet started or has already ended!");
      return;
    }
    fetch(`${API_URL}/lab-sessions/${sessionId}/end`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    })
      .then(res => res.json())
      .then(() => window.location.reload())
      .catch((err) => alert(`Error ending session: ${err.message || err}`));
  };

  const addSeatLine = () => {
    setSeatLines([...seatLines, { podId: '', seatId: '' }]);
  };

  const handleLineChange = (idx, field, value) => {
    const updated = [...seatLines];
    updated[idx][field] = value;
    setSeatLines(updated);
  };

  const checkIn = async () => {
    if (!studentId.trim()) {
      alert("Please fill in the student ID!");
      return;
    }
    if (session.status !== 'active') {
      alert("Check-in can only be done during an ongoing session!");
      return;
    }
    if (session.occupancy >= session.capacity) {
      alert("The lab is full!");
      return;
    }
    try {
      await api.post(
        `/lab-sessions/${sessionId}/checkin`,
        {
          studentId,
          seats: seatLines,
          borrowedItem
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        alert(error.response.data.error);
      } else {
        alert("An unexpected error occurred during check-in. Please try again.");
      }
    }
  };

  const checkOut = async () => {
    // If there are selected logs, perform batch checkout
    // Replace the Promise.all call with a sequential for-loop:
if (selectedLogIds.length > 0) {
  const batchStudentIds = selectedLogIds
    .map(id => {
      const log = studentLogs.find(l => l._id === id);
      return log ? log.idNumber : null;
    })
    .filter(Boolean);
  try {
    for (const sid of batchStudentIds) {
      await api.post(
        `/lab-sessions/${sessionId}/checkout`,
        { studentId: sid },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
    }
    alert("Batch checkout successful!");
    return;
  } catch (error) {
    alert("Batch checkout failed: " + (error.message || error));
    return;
  }
}
 else {
      // Normal individual checkout
      if (!studentId.trim()) {
        alert("Please fill in the student ID!");
        return;
      }
      const studentLog = studentLogs.find(
        log => log.idNumber.replace(/C/gi, '') === studentId.replace(/C/gi, '')
      );
      const studentName = studentLog ? studentLog.name : 'this student';
      const confirmCheckOut = window.confirm(
        `Are you sure to sign out the student ${studentName}?`
      );
      if (!confirmCheckOut) {
        alert("Check out has been cancelled.");
        return;
      }
      try {
        const response = await api.post(
          `/lab-sessions/${sessionId}/checkout`,
          { studentId },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        alert(response.data.message);
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          alert("An unexpected error occurred during check-out. Please try again.");
        }
      }
    }
  };

  const handleSeatClick = (podId, seatId) => {
    if (activeLineIndex === null) {
      alert("Please select a seat line (by focusing an input) before clicking a seat!");
      return;
    }
    const updated = [...seatLines];
    updated[activeLineIndex].podId = podId;
    updated[activeLineIndex].seatId = seatId;
    setSeatLines(updated);
  };

  const handleStudentIdClick = (idNumber) => {
    setStudentId(idNumber);
  };

  const handleCheckboxChange = (logId) => {
    setSelectedLogIds(prev =>
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  const downloadRecords = async (session) => {
    // Check if session is valid
    if (!session || !session._id) {
      alert("Session is undefined or missing _id.");
      return;
    }
    // Get supervisor directly from session
    const supervisor = session.supervisor;
    if (!supervisor) {
      alert("Supervisor field is missing!");
      return;
    }
    try {
      const response = await api.get(
        `/lab-sessions/${session._id}/records?supervisor=${encodeURIComponent(supervisor)}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
      // Check if the response data is empty
      if (!response.data || response.data.size === 0) {
        alert("No student records found for this session.");
        return;
      }
      let fileName = "session_records.csv";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].trim().replace(/['"]/g, "");
      }
      const blob = new Blob([response.data], { type: "text/csv" });
      const file = new File([blob], fileName, { type: "text/csv" });
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(file);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        alert("No student records found for this session.");
      } else {
        console.error("Error downloading records:", error);
        alert("Download failed.");
      }
    }
  };

  const fetchStudentLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/lab-sessions/${sessionId}/logs`);
      setStudentLogs(response.data);
    } catch (error) {
      console.error("Error fetching student logs:", error);
    }
  };

  const updateLog = async () => {
    if (!studentId.trim()) {
      alert("Please enter a valid Student ID!");
      return;
    }
    try {
      await axios.put(`${API_URL}/lab-sessions/student-logs/${sessionId}`, {
        studentId,
        seats: seatLines,
        borrowedItem,
        timeOut
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      fetchStudentLogs();
      alert("Student log updated successfully!");
      window.location.reload();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        alert(error.response.data.error);
      } else {
        alert("An unexpected error occurred. Please try again.");
      }
    }
  };

  const deleteLog = async (logId, timeOut) => {
    try {
      if (!timeOut) {
        alert("The student has not signed out yet, and the record cannot be deleted!");
        return;
      }
      // Find the log in studentLogs with the matching _id
      const logToDelete = studentLogs.find(log => log._id === logId);
      const studentName = logToDelete ? logToDelete.name : 'this student';
      const confirmDelete = window.confirm(
        `Are you sure you want to delete ${studentName}'s record?`
      );
      if (!confirmDelete) {
        alert("The operation has been canceled.");
        return;
      }
      await api.delete(`/lab-sessions/student-logs/${logId}`, { 
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } 
      });
      setStudentLogs(studentLogs.filter(log => log._id !== logId));
    } catch (error) {
      console.error("Error deleting student log:", error);
    }
  };
  

  const handleTimeOutClick = (theirTimeOut) => {
    if (!theirTimeOut) {
      alert("This student hasn't been checked out yet, no timeOut to copy!");
      return;
    }
    setTimeOut(new Date(theirTimeOut).toISOString());
  };

  const handleConfirmSupervisor = async () => {
    try {
      await api.put(`/lab-sessions/${session._id}/supervisor`, { supervisor: newSupervisor }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setSession({ ...session, supervisor: newSupervisor });
      setEditingSupervisor(false);
    } catch (error) {
      console.error("Error updating supervisor:", error);
    }
  };
  
  const handleCancelSupervisor = () => {
    setEditingSupervisor(false);
  };

  const renderPodLayout = (pod) => {
    switch (pod.direction) {
      case "horizontal-left":
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>
              Pod-{pod.podId}
            </Typography>
            <Box>
              <Box sx={{ display: 'flex' }}>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[0].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[0].seatId)}
                >
                  {pod.seats[0].seatId}
                </Button>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[1].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[1].seatId)}
                >
                  {pod.seats[1].seatId}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', mt: { xs: 0, md: 1 } }}>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[2].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[2].seatId)}
                >
                  {pod.seats[2].seatId}
                </Button>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[3].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[3].seatId)}
                >
                  {pod.seats[3].seatId}
                </Button>
              </Box>
            </Box>
          </Box>
        );
      case "horizontal-right":
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>
              <Box sx={{ display: 'flex' }}>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[0].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[0].seatId)}
                >
                  {pod.seats[0].seatId}
                </Button>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[1].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[1].seatId)}
                >
                  {pod.seats[1].seatId}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', mt: { xs: 0, md: 1 } }}>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[2].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[2].seatId)}
                >
                  {pod.seats[2].seatId}
                </Button>
                <Button
                  variant="text"
                  sx={{
                    backgroundColor: pod.seats[3].status === "Available" ? 'green' : 'red',
                    color: 'white',
                    minWidth: { xs: 'auto', md: '30px' },
                    p: { xs: '2px', md: '8px' }
                  }}
                  onClick={() => handleSeatClick(pod.podId, pod.seats[3].seatId)}
                >
                  {pod.seats[3].seatId}
                </Button>
              </Box>
            </Box>
            <Typography variant="subtitle2" sx={{ ml: 1 }}>
              Pod-{pod.podId}
            </Typography>
          </Box>
        );
      case "vertical":
        return (
          <Box>
            <Typography variant="subtitle2" align="center">
              Pod-{pod.podId}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 0, md: 1 } }}>
              <Button
                variant="text"
                sx={{
                  backgroundColor: pod.seats[0].status === "Available" ? 'green' : 'red',
                  color: 'white',
                  minWidth: { xs: 'auto', md: '30px' },
                  p: { xs: '2px', md: '8px' }
                }}
                onClick={() => handleSeatClick(pod.podId, pod.seats[0].seatId)}
              >
                {pod.seats[0].seatId}
              </Button>
              <Button
                variant="text"
                sx={{
                  backgroundColor: pod.seats[2].status === "Available" ? 'green' : 'red',
                  color: 'white',
                  minWidth: { xs: 'auto', md: '30px' },
                  p: { xs: '2px', md: '8px' }
                }}
                onClick={() => handleSeatClick(pod.podId, pod.seats[2].seatId)}
              >
                {pod.seats[2].seatId}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 0, md: 1 } }}>
              <Button
                variant="text"
                sx={{
                  backgroundColor: pod.seats[1].status === "Available" ? 'green' : 'red',
                  color: 'white',
                  minWidth: { xs: 'auto', md: '30px' },
                  p: { xs: '2px', md: '8px' }
                }}
                onClick={() => handleSeatClick(pod.podId, pod.seats[1].seatId)}
              >
                {pod.seats[1].seatId}
              </Button>
              <Button
                variant="text"
                sx={{
                  backgroundColor: pod.seats[3].status === "Available" ? 'green' : 'red',
                  color: 'white',
                  minWidth: { xs: 'auto', md: '30px' },
                  p: { xs: '2px', md: '8px' }
                }}
                onClick={() => handleSeatClick(pod.podId, pod.seats[3].seatId)}
              >
                {pod.seats[3].seatId}
              </Button>
            </Box>
          </Box>
        );
      case "professor-desk":
        return (
          <Box sx={{ width: { xs: 'auto', md: '120px' }, height: { xs: 'auto', md: '50px' }, backgroundColor: 'lightblue', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <Typography variant="subtitle2" align="center">
              Professor's Desk
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  if (!session) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      {/* Title of the session */}
      <Typography variant="h5" gutterBottom>
        {session?.lab ? ` ${session.lab.name}` : "Loading..."}
      </Typography>
  
      {/* Audio elements for alarms */}
      <audio ref={bellEndRef} src="/sounds/end.mp3" />
      <audio ref={bell30MinRef} src="/sounds/30min.mp3" />
      <audio ref={bellTestRef} src="/sounds/test.mp3" />
  
      {/* Tabs for switching between timer and sign in/out */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2 }}
      >
        <Tab value="timer" label="Timer" />
        <Tab value="sign" label="Sign" />
      </Tabs>
  
      {/* Render content based on the selected tab */}
      {activeTab === 'timer' ? (
        /* Timer tab */
        <Box className="timer" sx={{ p: 2, textAlign: 'left' }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Occupancy: {session.occupancy}/{session.lab.capacity}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Available Computers: {session.availableSeats}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Start: {new Date(adjustDateToDayOfWeek(session.scheduledStart, session.dayOfWeek))
              .toLocaleString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            End: {new Date(adjustDateToDayOfWeek(session.scheduledEnd, session.dayOfWeek))
              .toLocaleString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography component="div" variant="body1" sx={{ mb: 1 }}>
              Supervisor:{" "}
              {editingSupervisor ? (
                <TextField
                  size="small"
                  value={newSupervisor}
                  onChange={(e) => setNewSupervisor(e.target.value)}
                />
              ) : (
                session.supervisor || "N/A"
              )}
            </Typography>
            {editingSupervisor ? (
              <Box sx={{ ml: 1 }}>
                <Button variant="contained" size="small" onClick={handleConfirmSupervisor}>
                  Confirm
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelSupervisor}
                  sx={{ ml: 1 }}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setNewSupervisor(session.supervisor || "");
                  setEditingSupervisor(true);
                }}
                sx={{ ml: 1 }}
              >
                Edit
              </Button>
            )}
          </Box>
          <Button variant="outlined" sx={{ mr: 2 }} onClick={() => playAlarm(bellTestRef)}>
            Sound Test
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h1"   sx={{
              flexGrow: 1,
              fontSize: { xs: '4rem', md: '10rem' }, 
              textAlign: 'center',
              mb: 2,
              mt: 2,
              wordBreak: 'break-word', 
              display: 'inline-block', 
              whiteSpace: 'normal'     
            }}
          >
              <strong>{remainingTime}</strong>
            </Typography>
          </Box>
          <ButtonGroup variant="contained" fullWidth sx={{ mt: 2 }}>
            <Button onClick={startSession}>Start Session</Button>
            <Button onClick={endSession}>End Session</Button>
            <Button onClick={() => downloadRecords(session)}>Download Records</Button>
          </ButtonGroup>
        </Box>
      ) : (
        /* Sign (check in / check out) tab */
        <Box
          sx={{
            // Switch layout direction based on screen size
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            // In portrait (small screens), height is auto; in larger screens, fixed height
            height: { xs: 'auto', md: '600px' }
          }}
        >
          {/* Left side: form inputs and table */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Input section */}
            <Box sx={{ display: 'flex', width: '100%', gap: 2, alignItems: 'center' }}>
              <TextField
                variant="outlined"
                label="Student ID"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                variant="outlined"
                label="Pod ID"
                value={seatLines[0].podId}
                onFocus={() => setActiveLineIndex(0)}
                onChange={(e) => handleLineChange(0, 'podId', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                variant="outlined"
                label="Seat ID"
                value={seatLines[0].seatId}
                onFocus={() => setActiveLineIndex(0)}
                onChange={(e) => handleLineChange(0, 'seatId', e.target.value)}
                sx={{ flex: 1 }}
              />
              <Button variant="contained" onClick={addSeatLine} sx={{ flexShrink: 0 }}>
                +
              </Button>
            </Box>
  
            {/* Additional seat lines (if any) */}
            {seatLines.slice(1).map((line, idx) => (
              <Grid container spacing={2} alignItems="center" key={idx} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Pod ID"
                    value={line.podId}
                    onFocus={() => setActiveLineIndex(idx + 1)}
                    onChange={(e) => handleLineChange(idx + 1, 'podId', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Seat ID"
                    value={line.seatId}
                    onFocus={() => setActiveLineIndex(idx + 1)}
                    onChange={(e) => handleLineChange(idx + 1, 'seatId', e.target.value)}
                  />
                </Grid>
              </Grid>
            ))}
  
            {/* Borrowed item & timeOut input */}
            <Box sx={{ display: 'flex', width: '100%', gap: 2, mt: 2 }}>
              <TextField
                variant="outlined"
                label="Borrowed Item"
                value={borrowedItem}
                onChange={(e) => setBorrowedItem(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                variant="outlined"
                label="timeOut (e.g. 2025-03-26T14:00:00)"
                value={timeOut}
                onChange={(e) => setTimeOut(e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
  
            {/* Button group for check-in / check-out / update log */}
            <ButtonGroup variant="contained" fullWidth sx={{ mt: 2 ,mb: 2}}>
              <Button onClick={checkIn}>Check-In</Button>
              <Button onClick={checkOut}>Check-Out</Button>
              <Button onClick={updateLog}>Update Log</Button>
            </ButtonGroup>
  
            {/* Scrollable table card */}
            <Box className="card" sx={{ flex: 1, overflow: 'auto' }}>
              {/* Limit the table's maxHeight in portrait mode,
                  and let it be more flexible on larger screens */}
              <TableContainer sx={{ maxHeight: { xs: 300, md: '100%' } }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" padding="checkbox">
                        <Checkbox
                          checked={selectedLogIds.length === studentLogs.length && studentLogs.length > 0}
                          indeterminate={selectedLogIds.length > 0 && selectedLogIds.length < studentLogs.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLogIds(studentLogs.map(log => log._id));
                            } else {
                              setSelectedLogIds([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">ID Number</TableCell>
                      <TableCell align="center">Seats</TableCell>
                      <TableCell align="center">Borrowed Item</TableCell>
                      <TableCell align="center">Check-In Time</TableCell>
                      <TableCell align="center">Check-Out Time</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentLogs.map((log) => (
                      <TableRow
                        key={log._id}
                        sx={{ "&:hover": { backgroundColor: 'action.hover' } }}
                      >
                        <TableCell align="center" padding="checkbox">
                          <Checkbox
                            checked={selectedLogIds.includes(log._id)}
                            onChange={() => handleCheckboxChange(log._id)}
                          />
                        </TableCell>
                        <TableCell align="center">{log.name}</TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handleStudentIdClick(log.idNumber)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {log.idNumber}
                        </TableCell>
                        <TableCell align="center">
                          {Array.isArray(log.seats)
                            ? log.seats.map(s => `${s.podId}-${s.seatId}`).join(', ')
                            : "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          {log.borrowedItem || "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          {new Date(log.timeIn).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align="center"
                          onClick={() => handleTimeOutClick(log.timeOut)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {log.timeOut
                            ? new Date(log.timeOut).toLocaleString()
                            : "Still checked in"}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            onClick={() => deleteLog(log._id, log.timeOut)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
  
          {/* Right side: the canvas (pod layout) */}
          <Box className="canvas-container" sx={{ width: '100%' }}>
            <Box
              ref={canvasRef}
              className="canvas"
              sx={{ width: '100%', height: '100%', position: 'relative' }}
            >
              {session?.pods.map((pod) => (
                <Box
                  key={pod.podId}
                  className="pod"
                  sx={{ position: 'absolute', left: `${pod.x}px`, top: `${pod.y}px` }}
                >
                  {renderPodLayout(pod)}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
  
};

export default AssistantSessionDetails;
